import { Link } from 'react-router-dom'
import { Button, Chip, Divider, List, ListItem, ListItemIcon, ListItemText, Paper, Typography } from "@mui/material"
import Grid2 from "@mui/material/Unstable_Grid2"
import { useMediaQuery } from '@mui/material'
import { Check } from "lucide-react"


const toScale = (compact) => {
    if (compact === true) return 0.72
    if (typeof compact === 'number') return compact
    return 1
}

const styles = {
    paper: (name, s) => ({
        borderRadius: '32px',
        p: `${1 * s}rem ${3 * s}rem`,
        minHeight: `${460 * s}px`,
        border: name === 'Pro' ? '3px solid #ff1493' : 'none',
        transition: 'box-shadow 0.3s ease',
        '&:hover': {
            boxShadow: '0 0 20px rgba(255, 20, 147, 0.4), 0 0 40px rgba(255, 20, 147, 0.15)',
        },
    }),
    heading: (isMobile, s) => ({
        fontFamily: 'Catamaran',
        fontWeight: 800,
        fontSize: `${(isMobile ? 1.8 : 2) * s}rem`,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
    }),
    desc: (s) => ({
        fontFamily: 'inherit',
        fontWeight: 600,
        color: 'text.gray',
        fontSize: `${0.9 * s}rem`,
        mb: `${2.5 * s}rem`,
    }),
    price: (isMobile, s) => ({
        fontFamily: 'Arial',
        fontWeight: 700,
        fontSize: `${(isMobile ? 2 : 2.6) * s}rem`,
        mr: .5
    }),
    priceCrossedOut: (isMobile, s) => ({
        fontFamily: 'Arial',
        fontWeight: 500,
        color: "text.gray",
        fontSize: `${(isMobile ? 1.2 : 1.6) * s}rem`,
    }),
    priceInfo: (isMobile, s) => ({
        fontFamily: 'inherit',
        fontWeight: 500,
        color: 'text.gray',
        fontSize: `${(isMobile ? 1.02 : 0.9) * s}rem`,
        mb: '.5rem'
    }),
    tierFeatureCheckIcon: {
        m: 0,
        p: 0,
        color: '#ff78c1'
    },
    ctaButton: (s) => ({
        m: '1rem 0',
        p: `${0.9 * s}rem ${2 * s}rem`,
        fontFamily: 'inherit',
        backgroundColor: 'primary.main',
        color: '#fff',
        textTransform: 'none',
        boxShadow: "4px 5px 0px #000",
        borderRadius: '40px',
        width: '100%',
    })
}

export default function PriceCard({
    tier,
    isAnnual,
    buttonAction = '/signup',
    buttonText = 'Try Free for 14 Days',
    compact = false
}) {
    const isMobile = useMediaQuery('(max-width:950px)')
    const s = toScale(compact)
    const { name, desc, priceMo, priceAnnual, features } = tier
    const isFree = priceMo === 0

    const buttonProps = typeof buttonAction === 'string'
        ? {
            component: Link,
            to: buttonAction
        }
        : {
            onClick: buttonAction
        }

    return (
        <Grid2 xs={12} md={5}>
            <Paper elevation={4} sx={styles.paper(name, s)}>
                <section>
                    <Typography
                        variant="h6"
                        component="h4"
                        sx={styles.heading(isMobile, s)}
                    >
                        {name}
                        {/* {
                            name === 'Pro' &&
                            <Chip
                                label="Best Value"
                                color="primary"
                                size={s < 1 ? "small" : "medium"}
                                sx={{ boxShadow: "1px 2px 0px #000" }}
                            />
                        } */}
                    </Typography>
                    <Typography
                        variant="body1"
                        component="p"
                        sx={styles.desc(s)}>
                        {desc}
                    </Typography>
                    {
                        isAnnual ?
                            isFree
                                ?
                                (<span>
                                    <Typography
                                        variant="h6"
                                        component="h6"
                                        sx={styles.price(isMobile, s)}
                                    >
                                        Free
                                    </Typography>
                                </span>)
                                :
                                (<span>
                                    <Typography
                                        variant="p"
                                        component="body1"
                                        sx={styles.priceCrossedOut(isMobile, s)}
                                    >
                                        <s>${priceMo}/mo</s>
                                    </Typography>

                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                    }}>
                                        <Typography
                                            variant="h6"
                                            component="h6"
                                            sx={styles.price(isMobile, s)}
                                        >
                                            ${Number(priceAnnual / 12).toFixed(1)}
                                        </Typography>
                                        <Chip
                                            label="2 Months Free"
                                            size="medium"
                                            sx={{ background: "#38b000", color: "#fff", m: '0 .4rem' }}
                                        />
                                    </div>
                                </span>)
                            :
                            <Typography
                                variant="h5"
                                component="h4"
                                sx={styles.price(isMobile, s)}
                            >
                                {isFree ? 'Free' : `$${priceMo}`}
                            </Typography>
                    }
                    <Typography
                        variant="body1"
                        component="p"
                        sx={styles.priceInfo(isMobile, s)}>
                        {
                            isFree ?
                                'free, forever'
                                :
                                isAnnual ? `$${priceAnnual} per year, billed annually` : 'per month, billed monthly'
                        }

                    </Typography>
                </section>
                <Divider />
                <List sx={{ m: '.5rem 0' }}>
                    {features.map(feature =>
                        <ListItem key={feature} disablePadding sx={{ m: 0, p: 0 }}>
                            {feature.length > 1 ?
                                <ListItemIcon>
                                    <Check style={styles.tierFeatureCheckIcon} />
                                </ListItemIcon>
                                :
                                <p></p>
                            }
                            <ListItemText
                                primary={feature}
                                primaryTypographyProps={s < 1 ? { fontSize: `${1 * s}rem` } : undefined}
                            />
                        </ListItem>
                    )}
                </List>
                <Divider />
                <Button
                    type="button"
                    variant="contained"
                    disableElevation
                    {...buttonProps}
                    sx={styles.ctaButton(s)}
                >
                    <span style={{ fontSize: `${1 * s}rem`, fontWeight: 700 }}>
                        {tier.cta || (isFree ? 'Get Started Free' : 'Try Free for 14 Days')}
                    </span>
                </Button>
            </Paper>
        </Grid2>
    )
}
